import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "medical-web-design-colorado",
  metaTitle: "Medical Practice Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom medical web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Medical practice marketing", to: "/medical-marketing-colorado" },
  ],
  currentLabel: "Medical Practice Web Design Colorado",
  eyebrow: "Medical Practice Web Design — Colorado",
  h1: (
    <>
      Medical Practice <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded medical websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Medical websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first medical sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Medical Practice web design in Colorado",
    body: "Medical Practice web design is the practice of building conversion-focused, mobile-first websites for medical companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with ADA-compliant accessibility shipped at launch." },
    { title: "Service pages per specialty", body: "Each procedure, condition, and treatment gets its own ranking page with proper medical schema." },
    { title: "HIPAA-aware intake", body: "Forms scoped to non-PHI fields with secure handoff to your EHR or scheduling platform." },
    { title: "Provider bio pages", body: "Each physician gets a full bio page with credentials, schema, and direct booking CTA." },
    { title: "Insurance & payment transparency", body: "Accepted plans and self-pay pricing surfaced where patients look for it." },
    { title: "Schema and SEO baked in", body: "Physician, MedicalBusiness, Service, FAQPage, Review schema from day one." },
  ],
  whyThisMarket: [
    { heading: "Patients search by condition, not specialty", body: "We build pages for the conditions and symptoms patients actually type, not just the specialty name." },
    { heading: "Accessibility is non-negotiable", body: "WCAG 2.1 AA compliance baked in — medical sites are a frequent ADA lawsuit target." },
    { heading: "Trust signals decide the call", body: "Board certifications, hospital affiliations, and patient reviews surfaced above the fold." },
  ],
  relatedLinks: [
    { to: "/medical-marketing-colorado", label: "Medical practice marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/medical-seo-colorado", label: "Medical Practice SEO in Colorado" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado medical case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a medical website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a medical website?", a: "Typical timeline is 4–6 weeks for a 12–20 page medical site with full service and service-area pages. Larger multi-location medical companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs medical companies?", a: "No — we build medical sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Medical Practice Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for medical companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/medical-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
